import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useLocation, Link } from "wouter";
import { MapPin, Mail, Lock, Eye, EyeOff, ArrowRight, Facebook, User, Phone } from "lucide-react";
import { FaApple } from "react-icons/fa";
import type { SignupInput } from "@workspace/api-client-react";
import { TownSelect } from "@/components/TownSelect";

const RETURN_TO_KEY = "ah_return_to";

/**
 * Only same-origin relative paths are honoured, so a crafted
 * `?returnTo=https://evil.example` (or the protocol-relative `//evil.example`,
 * or `/\evil.example` which browsers normalise to a host) can never be used to
 * bounce a freshly authenticated user off-site. Returning to /login itself is
 * also rejected — that would just loop the user back to this page.
 */
function safePath(raw: string | null | undefined): string | null {
  if (!raw) return null;
  if (!raw.startsWith("/")) return null;
  if (raw.startsWith("//") || raw.startsWith("/\\")) return null;
  if (raw === "/login" || raw.startsWith("/login?") || raw.startsWith("/login/")) return null;
  return raw;
}

function readStoredReturnTo(): string | null {
  try {
    return window.sessionStorage.getItem(RETURN_TO_KEY);
  } catch {
    return null;
  }
}

function clearStoredReturnTo() {
  try {
    window.sessionStorage.removeItem(RETURN_TO_KEY);
  } catch { /* private mode — nothing to clean up */ }
}

/**
 * Drop returnTo/redirect from the address bar before navigating away so the
 * back button lands on a clean /login instead of immediately re-triggering the
 * redirect once the user is already signed in.
 */
function stripReturnToFromUrl() {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has("returnTo") && !url.searchParams.has("redirect")) return;
  url.searchParams.delete("returnTo");
  url.searchParams.delete("redirect");
  window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
}

type Mode = "signin" | "signup";

type FBSdk = {
  login: (cb: (r: { authResponse?: { accessToken: string } }) => void, opts: { scope: string }) => void;
};

function loadFacebookSDK(appId: string): Promise<void> {
  return new Promise((resolve) => {
    const w = window as unknown as { FB?: FBSdk; fbAsyncInit?: () => void };
    if (w.FB) { resolve(); return; }
    w.fbAsyncInit = () => {
      (window as unknown as { FB: { init: (c: object) => void } }).FB.init({
        appId, cookie: true, xfbml: false, version: "v19.0",
      });
      resolve();
    };
    const s = document.createElement("script");
    s.src = "https://connect.facebook.net/en_US/sdk.js";
    s.async = true; s.defer = true;
    document.head.appendChild(s);
  });
}

export default function Login() {
  const params = new URLSearchParams(
    typeof window !== "undefined" ? window.location.search : ""
  );
  const initialMode: Mode = params.get("mode") === "create" ? "signup" : "signin";

  // Where to send the user after a successful login (e.g. back to the deal they
  // were trying to redeem). `redirect` is the older param name still emitted by
  // the store-invite flow. Only same-origin relative paths are honoured.
  const safeReturnTo = safePath(params.get("returnTo") ?? params.get("redirect"));

  const [mode, setMode] = useState<Mode>(initialMode);

  // Sign-in fields
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Sign-up extra fields
  const [name, setName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [town, setTown] = useState("Ayr");

  const { login, loginWithToken, signup } = useAuth();
  const [, setLocation] = useLocation();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const goAfterLogin = useCallback(() => {
    // The social-login round trip wipes the query string, so the target is
    // stashed in sessionStorage before we hand off to the provider.
    const target = safeReturnTo ?? safePath(readStoredReturnTo());
    clearStoredReturnTo();
    stripReturnToFromUrl();
    setLocation(target ?? "/");
  }, [safeReturnTo, setLocation]);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const socialSuccess = p.get("social_success");
    const socialError = p.get("social_error");

    if (socialSuccess === "1") {
      window.history.replaceState({}, "", window.location.pathname);
      fetch("/api/auth/social/session", { credentials: "include" })
        .then((r) => r.json())
        .then((data: { token?: string; error?: string }) => {
          if (data.token) {
            loginWithToken(data.token).then(() => goAfterLogin());
          } else {
            setError("Social sign-in failed. Please try again.");
          }
        })
        .catch(() => setError("Social sign-in failed. Please try again."));
      return;
    }
    if (socialError) {
      const messages: Record<string, string> = {
        cancelled: "Sign-in was cancelled.",
        token_failed: "Could not complete sign-in. Please try again.",
        server_error: "A server error occurred. Please try again.",
        state_mismatch: "Sign-in session expired or was tampered with. Please try again.",
      };
      setError(messages[socialError] ?? "Social sign-in failed.");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [loginWithToken, goAfterLogin]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login({ email, password });
      goAfterLogin();
    } catch (err: unknown) {
      setError((err as Error).message || "Incorrect email or password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(signupEmail)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (signupPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (signupPassword !== confirmPassword) {
      setError("Passwords do not match. Please try again.");
      return;
    }
    setLoading(true);
    try {
      const data: SignupInput = { name, email: signupEmail, password: signupPassword, role: "resident", town, phone };
      await signup(data);
      goAfterLogin();
    } catch (err: unknown) {
      setError((err as Error).message || "This email may already be registered. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = (provider: "google" | "apple/web") => {
    try {
      if (safeReturnTo) window.sessionStorage.setItem(RETURN_TO_KEY, safeReturnTo);
      else clearStoredReturnTo();
    } catch { /* private mode — fall back to the homepage after sign-in */ }
    window.location.href = `/api/auth/${provider}`;
  };

  const handleFacebook = async () => {
    setError("");
    setLoading(true);
    try {
      const configRes = await fetch("/api/auth/social/public-config");
      const config = await configRes.json() as { facebookAppId?: string | null };
      if (!config.facebookAppId) {
        setError("Facebook login is not configured yet.");
        setLoading(false);
        return;
      }
      await loadFacebookSDK(config.facebookAppId);
      const fb = (window as unknown as { FB: FBSdk }).FB;
      fb.login((response) => {
        if (!response.authResponse?.accessToken) {
          setError("Facebook sign-in was cancelled.");
          setLoading(false);
          return;
        }
        fetch("/api/auth/oauth/facebook", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ accessToken: response.authResponse.accessToken }),
        })
          .then((r) => r.json())
          .then(async (data: { token?: string; error?: string }) => {
            if (!data.token) throw new Error(data.error ?? "Facebook sign-in failed.");
            await loginWithToken(data.token);
            goAfterLogin();
          })
          .catch((err: unknown) => setError((err as Error).message ?? "Facebook sign-in failed."))
          .finally(() => setLoading(false));
      }, { scope: "public_profile,email" });
    } catch {
      setError("Facebook sign-in failed. Please try again.");
      setLoading(false);
    }
  };

  const switchMode = (m: Mode) => {
    setMode(m);
    setError("");
  };

  return (
    <div className="flex items-start justify-center min-h-[80vh] px-4 py-12 bg-slate-50">
      <div className="w-full max-w-sm">

        {/* Logo */}
        <div className="text-center mb-7">
          <div className="w-16 h-16 rounded-2xl bg-primary flex items-center justify-center mx-auto mb-4 shadow-lg">
            <MapPin className="text-amber-400" size={26} />
          </div>
          <h1 className="text-2xl font-bold text-primary">
            {mode === "signin" ? "Welcome back" : "Join Ayrshire Hub"}
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {mode === "signin"
              ? "Sign in to your Ayrshire Hub account"
              : "Free to join — takes less than a minute"}
          </p>
        </div>

        {/* Mode toggle */}
        <div className="flex bg-slate-100 rounded-xl p-1 mb-6">
          <button
            type="button"
            onClick={() => switchMode("signin")}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${
              mode === "signin"
                ? "bg-white text-primary shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            Sign In
          </button>
          <button
            type="button"
            onClick={() => switchMode("signup")}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${
              mode === "signup"
                ? "bg-white text-primary shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            Create Account
          </button>
        </div>

        {/* Social buttons */}
        <div className="space-y-3 mb-5">
          <SocialButton
            onClick={() => handleSocialLogin("apple/web")}
            bg="bg-black hover:bg-slate-800"
            border=""
            textColor="text-white"
            icon={<FaApple size={20} aria-hidden="true" />}
            label={mode === "signin" ? "Continue with Apple" : "Sign up with Apple"}
          />
          <SocialButton
            onClick={() => handleSocialLogin("google")}
            bg="bg-white hover:bg-slate-50"
            border="border border-slate-200"
            textColor="text-slate-700"
            icon={<span className="text-[#4285f4] font-bold text-base w-5 text-center">G</span>}
            label={mode === "signin" ? "Continue with Google" : "Sign up with Google"}
          />
          <SocialButton
            onClick={handleFacebook}
            bg="bg-[#1877f2] hover:bg-[#1565d8]"
            border=""
            textColor="text-white"
            icon={<Facebook size={17} className="text-white" />}
            label={mode === "signin" ? "Continue with Facebook" : "Sign up with Facebook"}
          />
          <p className="text-center text-xs text-slate-400 leading-relaxed">
            Sign in or create an account with Apple, Google or Facebook.
          </p>
        </div>

        <div className="flex items-center gap-3 mb-5">
          <div className="flex-1 h-px bg-slate-200" />
          <span className="text-xs text-slate-400">or use email</span>
          <div className="flex-1 h-px bg-slate-200" />
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-100 text-red-700 text-sm px-4 py-3 rounded-xl mb-4">
            {error}
          </div>
        )}

        {/* Sign In form */}
        {mode === "signin" && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden mb-4">
            <form onSubmit={handleSignIn}>
              <div className="px-4 py-3">
                <label htmlFor="signin-email" className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block mb-1.5">
                  Email address
                </label>
                <div className="flex items-center gap-3">
                  <Mail size={15} className="text-slate-400 flex-shrink-0" aria-hidden="true" />
                  <input
                    id="signin-email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 text-sm text-slate-800 placeholder:text-slate-400 outline-none bg-transparent py-0.5"
                  />
                </div>
              </div>
              <div className="h-px bg-slate-100 ml-4" />
              <div className="px-4 py-3">
                <label htmlFor="signin-password" className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block mb-1.5">
                  Password
                </label>
                <div className="flex items-center gap-3">
                  <Lock size={15} className="text-slate-400 flex-shrink-0" aria-hidden="true" />
                  <input
                    id="signin-password"
                    type={showPassword ? "text" : "password"}
                    required
                    autoComplete="current-password"
                    placeholder="Your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="flex-1 text-sm text-slate-800 placeholder:text-slate-400 outline-none bg-transparent py-0.5"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="text-slate-400 hover:text-slate-600 flex-shrink-0" aria-label={showPassword ? "Hide password" : "Show password"}>
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className="px-4 pb-4 pt-1">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2.5 bg-primary hover:bg-primary/90 text-white font-bold py-3.5 rounded-xl transition-opacity disabled:opacity-70"
                >
                  {loading ? (
                    <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>Sign In <ArrowRight size={18} /></>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Sign Up form */}
        {mode === "signup" && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden mb-4">
            <form onSubmit={handleSignUp}>
              <div className="px-4 py-3">
                <label htmlFor="signup-name" className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block mb-1.5">
                  Full name
                </label>
                <div className="flex items-center gap-3">
                  <User size={15} className="text-slate-400 flex-shrink-0" aria-hidden="true" />
                  <input
                    id="signup-name"
                    required
                    autoComplete="name"
                    placeholder="John Smith"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="flex-1 text-sm text-slate-800 placeholder:text-slate-400 outline-none bg-transparent py-0.5"
                  />
                </div>
              </div>
              <div className="h-px bg-slate-100 ml-4" />
              <div className="px-4 py-3">
                <label htmlFor="signup-email" className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block mb-1.5">
                  Email address
                </label>
                <div className="flex items-center gap-3">
                  <Mail size={15} className="text-slate-400 flex-shrink-0" aria-hidden="true" />
                  <input
                    id="signup-email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={signupEmail}
                    onChange={(e) => setSignupEmail(e.target.value)}
                    className="flex-1 text-sm text-slate-800 placeholder:text-slate-400 outline-none bg-transparent py-0.5"
                  />
                </div>
              </div>
              <div className="h-px bg-slate-100 ml-4" />
              <div className="px-4 py-3">
                <label htmlFor="signup-phone" className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block mb-1.5">
                  Mobile number
                </label>
                <div className="flex items-center gap-3">
                  <Phone size={15} className="text-slate-400 flex-shrink-0" aria-hidden="true" />
                  <input
                    id="signup-phone"
                    type="tel"
                    required
                    autoComplete="tel"
                    placeholder="e.g. 07700 900000"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="flex-1 text-sm text-slate-800 placeholder:text-slate-400 outline-none bg-transparent py-0.5"
                  />
                </div>
              </div>
              <div className="h-px bg-slate-100 ml-4" />
              <div className="px-4 py-3">
                <label htmlFor="signup-password" className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block mb-1.5">
                  Password
                </label>
                <div className="flex items-center gap-3">
                  <Lock size={15} className="text-slate-400 flex-shrink-0" aria-hidden="true" />
                  <input
                    id="signup-password"
                    type={showPassword ? "text" : "password"}
                    required
                    autoComplete="new-password"
                    placeholder="Min. 8 characters"
                    value={signupPassword}
                    onChange={(e) => setSignupPassword(e.target.value)}
                    className="flex-1 text-sm text-slate-800 placeholder:text-slate-400 outline-none bg-transparent py-0.5"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="text-slate-400 hover:text-slate-600 flex-shrink-0" aria-label={showPassword ? "Hide password" : "Show password"}>
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className="h-px bg-slate-100 ml-4" />
              <div className="px-4 py-3">
                <label htmlFor="signup-confirm-password" className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block mb-1.5">
                  Confirm password
                </label>
                <div className="flex items-center gap-3">
                  <Lock size={15} className="text-slate-400 flex-shrink-0" aria-hidden="true" />
                  <input
                    id="signup-confirm-password"
                    type={showPassword ? "text" : "password"}
                    required
                    autoComplete="new-password"
                    placeholder="Repeat your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="flex-1 text-sm text-slate-800 placeholder:text-slate-400 outline-none bg-transparent py-0.5"
                  />
                </div>
              </div>
              <div className="h-px bg-slate-100 ml-4" />
              <div className="px-4 py-3">
                <label htmlFor="signup-town" className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block mb-2">
                  Your town
                </label>
                <TownSelect id="signup-town" value={town} onChange={setTown} />
              </div>
              <div className="px-4 pb-4 pt-1">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2.5 bg-primary hover:bg-primary/90 text-white font-bold py-3.5 rounded-xl transition-opacity disabled:opacity-70"
                >
                  {loading ? (
                    <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>Create Account <ArrowRight size={18} /></>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {mode === "signin" && (
          <p className="text-center text-xs text-slate-400 mb-2">
            <Link href="/forgot-password" className="hover:text-primary hover:underline">
              Forgot your password?
            </Link>
          </p>
        )}

        {mode === "signup" && (
          <p className="text-xs text-slate-400 text-center leading-relaxed mb-2">
            By registering you agree to our Terms of Service. Your data is never sold to third parties.
          </p>
        )}
      </div>
    </div>
  );
}

function SocialButton({
  onClick, bg, border, textColor, icon, label,
}: {
  onClick: () => void;
  bg: string;
  border: string;
  textColor: string;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-[13px] ${bg} ${border} transition-colors shadow-sm`}
    >
      <span className="w-5 flex-shrink-0 flex items-center justify-center">{icon}</span>
      <span className={`flex-1 text-center text-sm font-semibold ${textColor}`}>{label}</span>
    </button>
  );
}
